<template>
  <div class="card mt-4">
    <div class="card-header pb-0">
      <h6 class="mb-0">Messages</h6>
      <p class="text-sm text-secondary mb-0">Review email and SMS activity across locations and organizations.</p>
    </div>
    <div class="card-body">
      <MaterialButton type="button" color="info" size="sm" @click="openModal">View Messages</MaterialButton>
    </div>

    <ContentModal v-model="modalOpen" title="Messages" size="2xl">
      <div class="d-flex flex-wrap gap-2 mb-3">
        <label class="text-sm text-secondary mb-0 d-flex align-items-center gap-1">From <input v-model="dateRange.start_date" type="date" class="form-control form-control-sm" @change="fetchMessages(1)"></label>
        <label class="text-sm text-secondary mb-0 d-flex align-items-center gap-1">To <input v-model="dateRange.end_date" type="date" class="form-control form-control-sm" @change="fetchMessages(1)"></label>
        <select v-model="filters.location_id" class="form-select form-select-sm" style="max-width:240px" @change="fetchMessages(1)">
          <option value="">All Locations</option>
          <option v-for="location in filterOptions.locations" :key="location.id" :value="location.id">{{ location.name }}</option>
        </select>
        <select v-model="filters.organization_id" class="form-select form-select-sm" style="max-width:240px" @change="fetchMessages(1)">
          <option value="">All Organizations</option>
          <option v-for="organization in filterOptions.organizations" :key="organization.id" :value="organization.id">{{ organization.name }}</option>
        </select>
      </div>
      <div class="d-flex flex-wrap align-items-center gap-2 mb-3">
        <span class="text-sm text-secondary me-1"><i class="material-icons text-sm align-middle me-1">filter_list</i>Type:</span>
        <label v-for="type in messageTypeOptions" :key="type.value" class="form-check form-check-inline mb-0">
          <input class="form-check-input" type="checkbox" :value="type.value" v-model="selectedMessageTypes" @change="applyTypeFilter">
          <span class="form-check-label text-sm">{{ type.label }}</span>
        </label>
      </div>

      <div class="row g-2 mb-3">
        <div v-for="type in messageTypeOptions" :key="type.value" class="col-6 col-md-2">
          <div class="card p-2 h-100">
            <small class="text-secondary">{{ type.label }}</small>
            <strong class="fs-5">{{ categoryCounts[type.value] || 0 }}</strong>
          </div>
        </div>
      </div>

      <BaseTable
        heading="Messages"
        :rows="rows"
        :columns="columns"
        idKey="message_id"
        searchable
        :selectable="false"
        :loading="loading"
        :pagination="pagination"
        @changePage="fetchMessages"
        @updatePageSize="onPageSizeChange"
        @searchChanged="onSearchChanged"
      >
        <template #buttons>
          <span class="text-xs text-secondary">Click a row action to view details</span>
        </template>
        <template #col-message_data="{ row }">
          <span class="text-truncate d-inline-block" style="max-width:240px" :title="stripHtml(row.message_data)">{{ truncate(stripHtml(row.message_data), 70) }}</span>
        </template>
        <template #col-delivery_status="{ row }">
          <div class="d-flex align-items-center gap-1">
            <span v-if="getStatusBadge(row)" :class="getStatusBadgeClass(getStatusBadge(row).category)">
              {{ getStatusBadge(row).emoji }} {{ getStatusBadge(row).label }}
            </span>
            <button
              v-if="row.message_type === 'email' || row.message_type === 'sms'"
              type="button"
              class="btn btn-sm btn-outline-secondary my-0 py-0 px-1"
              title="Check delivery status"
              :disabled="checkingStatusFor === row.message_id"
              @click.stop="checkMessageStatus(row)"
            >
              <span v-if="checkingStatusFor === row.message_id" class="spinner-border spinner-border-sm" style="width:10px;height:10px"></span>
              <i v-else class="material-icons" style="font-size:14px;vertical-align:middle">{{ getStatusBadge(row) ? 'refresh' : 'search' }}</i>
            </button>
          </div>
        </template>
        <template #actions="{ row }">
          <button type="button" class="btn btn-sm btn-outline-info my-0 py-1 px-2" title="View message details" @click="openMessageDetails(row)">
            <i class="material-icons text-sm">info</i>
          </button>
        </template>
      </BaseTable>
    </ContentModal>

    <ContentModal v-model="detailsOpen" title="Message Details" size="lg">
      <div v-if="selectedMessage" class="message-details">
        <dl class="log-details-list">
          <div class="log-item"><dt>Date</dt><dd>{{ selectedMessage.message_dt }}</dd></div>
          <div class="log-item"><dt>Subject</dt><dd>{{ selectedMessage.message_subject || '-' }}</dd></div>
          <div class="log-item"><dt>Type</dt><dd>{{ selectedMessage.message_type }}</dd></div>
          <div class="log-item"><dt>Direction</dt><dd>{{ selectedMessage.message_direction || '-' }}</dd></div>
          <div class="log-item"><dt>Recipient</dt><dd>{{ selectedMessage.recipient_address || '-' }}</dd></div>
          <div class="log-item"><dt>Location</dt><dd>{{ selectedMessage.location || '-' }}</dd></div>
          <div class="log-item"><dt>Organization</dt><dd>{{ selectedMessage.organization || '-' }}</dd></div>
          <div class="log-item"><dt>Cost</dt><dd>${{ selectedMessage.rate ?? 0 }}</dd></div>
          <div class="log-item"><dt>Message</dt><dd class="log-message" style="white-space:pre-wrap;word-break:break-word">{{ stripHtml(selectedMessage.message_data) }}</dd></div>
          <div v-if="selectedMessage.message_type === 'email' || selectedMessage.message_type === 'sms'" class="log-item">
            <dt>Delivery Status</dt>
            <dd>
              <span v-if="getStatusBadge(selectedMessage)" :class="getStatusBadgeClass(getStatusBadge(selectedMessage).category)">
                {{ getStatusBadge(selectedMessage).emoji }} {{ getStatusBadge(selectedMessage).label }}
              </span>
              <span v-else class="text-secondary">Not checked</span>
              <div v-if="getStatusSummaryLines(selectedMessage).length" class="mt-2 text-sm text-secondary">
                <div v-for="line in getStatusSummaryLines(selectedMessage)" :key="line">{{ line }}</div>
              </div>
              <button type="button" class="btn btn-sm btn-outline-secondary mt-2 mb-0" :disabled="checkingStatusFor === selectedMessage.message_id" @click="checkMessageStatus(selectedMessage)">
                {{ checkingStatusFor === selectedMessage.message_id ? 'Checking...' : (getStatusBadge(selectedMessage) ? 'Refresh Status' : 'Check Status') }}
              </button>
            </dd>
          </div>
        </dl>
      </div>
    </ContentModal>
  </div>
</template>

<script setup>
import { reactive, ref } from 'vue'
import BaseTable from '@/components/Table/Table.vue'
import ContentModal from '@/components/Modal/ContentModal.vue'
import MaterialButton from '@/components/common/MaterialButton.vue'
import api from '@/config/api'

const modalOpen = ref(false)
const loading = ref(false)
const rows = ref([])
const search = ref('')
const filters = reactive({ location_id: '', organization_id: '' })
function dateInput(date) { return date.toISOString().slice(0, 10) }
const dateRange = reactive({
  start_date: dateInput(new Date(Date.now() - 86400000)),
  end_date: dateInput(new Date()),
})
const selectedMessageTypes = ref(['email', 'sms'])
const messageTypeOptions = [
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
  { value: 'ai_chat', label: 'AI Chat' },
  { value: 'sms_ai_chat', label: 'SMS AI Chat' },
  { value: 'ai_scheduler', label: 'AI Scheduler' },
]
const filterOptions = reactive({ locations: [], organizations: [] })
const categoryCounts = reactive({})
const detailsOpen = ref(false)
const selectedMessage = ref(null)
const checkingStatusFor = ref(null)
const pagination = ref({ currentPage: 1, pageSize: 10, totalRecords: 0, totalPages: 0, hasPrevious: false, hasNext: false })
const columns = [
  { key: 'message_dt', label: 'DATE' },
  { key: 'message_type', label: 'TYPE' },
  { key: 'message_subject', label: 'SUBJECT' },
  { key: 'message_data', label: 'MESSAGE' },
  { key: 'recipient_address', label: 'RECIPIENT' },
  { key: 'location', label: 'LOCATION' },
  { key: 'organization', label: 'ORGANIZATION' },
  { key: 'delivery_status', label: 'STATUS' },
]

async function fetchMessages(page = 1, pageSize = pagination.value.pageSize) {
  loading.value = true
  try {
    const response = await api.get('/admin/messages', {
      params: {
        page_number: page,
        page_size: pageSize,
        search: search.value,
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
        message_type_filter: selectedMessageTypes.value.join(','),
        ...filters,
      },
    })
    rows.value = response.data?.output || []
    const pag = response.data?.pagination || {}
    pagination.value = {
      currentPage: pag.current_page || 1,
      pageSize: pag.page_size || pageSize,
      totalRecords: pag.total_records || 0,
      totalPages: pag.total_pages || 0,
      hasPrevious: pag.has_previous || false,
      hasNext: pag.has_next || false,
    }
    filterOptions.locations = response.data?.filters?.locations || filterOptions.locations
    filterOptions.organizations = response.data?.filters?.organizations || filterOptions.organizations
    Object.keys(categoryCounts).forEach((key) => delete categoryCounts[key])
    Object.assign(categoryCounts, response.data?.category_counts || {})
  } catch (error) {
    console.error('Error fetching admin messages:', error)
    rows.value = []
  } finally {
    loading.value = false
  }
}

function openModal() {
  modalOpen.value = true
  fetchMessages()
}
function onPageSizeChange(size) { fetchMessages(1, size) }
function onSearchChanged(value) { search.value = value || ''; fetchMessages(1) }
function applyTypeFilter() { fetchMessages(1) }
async function openMessageDetails(row) {
  selectedMessage.value = row
  detailsOpen.value = true
  try {
    const response = await api.get(`/admin/messages/${row.message_id}`)
    if (selectedMessage.value?.message_id === row.message_id) {
      selectedMessage.value.message_data = response.data?.message_data || ''
    }
  } catch (error) {
    console.error('Error fetching message details:', error)
  }
}
function getStatusBadge(row) {
  if (!row.status_output) return null
  try {
    const data = JSON.parse(row.status_output)
    return {
      label: data.status_label || data.status || 'Unknown',
      emoji: data.status_emoji || '',
      category: data.status_category || 'Unknown',
    }
  } catch { return null }
}
function getStatusBadgeClass(category) {
  if (category === 'Successful') return 'badge bg-gradient-success'
  if (category === 'Failed') return 'badge bg-gradient-danger'
  if (category === 'Issues') return 'badge bg-gradient-warning'
  if (category === 'Pending') return 'badge bg-gradient-info'
  return 'badge bg-gradient-secondary'
}
function getStatusSummaryLines(row) {
  if (!row.status_output) return []
  try { return JSON.parse(row.status_output).summary || [] } catch { return [] }
}
async function checkMessageStatus(row) {
  if (!row?.location_id || !row?.message_id) return
  checkingStatusFor.value = row.message_id
  try {
    const response = await api.get(`/locations/${row.location_id}/messages/${row.message_id}/status`)
    row.status_output = JSON.stringify(response.data)
  } catch (error) {
    console.error('Error checking message status:', error)
  } finally {
    checkingStatusFor.value = null
  }
}
function stripHtml(value) { return String(value || '').replace(/<[^>]*>/g, ' ') }
function truncate(value, length) { return value.length > length ? `${value.slice(0, length)}...` : value }
</script>
